/**
 * Flickity carousel initialization and lifecycle management hook
 * Handles Flickity instance creation, resize handling, and portal rendering for carousel controls
 */

import Flickity from 'flickity'
import { ComponentChildren } from 'preact'
import { createPortal } from 'preact/compat'
import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import debounce from 'lodash.debounce'
import {
  CHEVRON_ARROW_SHAPE,
  flickityChevronDefaultOptions,
  syncFlickityChevronArrows
} from '@/components/helpers/flickity-chevrons'

const defaultOptions: Flickity.Options = {
  contain: true,
  resize: false,
  ...flickityChevronDefaultOptions
}

const resizeListener = (flickity: Flickity) => {
  const minHeightElements = flickity.slider.querySelectorAll('.min-h-full')
  minHeightElements.forEach((element: Element) => {
    element.classList.remove('min-h-full')
  })
  flickity.resize()
  flickity.reloadCells()
  minHeightElements.forEach((element: Element) => {
    element.classList.add('min-h-full')
  })
}

export function useFlickity(target: Element, options: Flickity.Options, deps: any[] = []) {
  const flickityRef = useRef<Flickity | null>(null)
  const sliderRef = useRef<Element | null>(null)
  const [isReady, setIsReady] = useState(false)
  const flickityOptions: Flickity.Options = {
    ...defaultOptions,
    ...options,
    arrowShape: CHEVRON_ARROW_SHAPE
  }

  useEffect(() => {
    if (!target) return

    setIsReady(false)
    let fallbackTimer: NodeJS.Timeout | null = null
    const handleResize = debounce(() => {
      if (flickityRef.current) {
        resizeListener(flickityRef.current)
      }
    }, 100)

    requestAnimationFrame(() => {
      const computedHeight = target.getBoundingClientRect().height
      if (computedHeight > 0) {
        (target as HTMLElement).style.minHeight = `${computedHeight}px`
      }

      flickityRef.current = new Flickity(target, flickityOptions)
      syncFlickityChevronArrows(target)

      let readyFired = false
      const handleReady = () => {
        if (readyFired) return
        readyFired = true
        syncFlickityChevronArrows(target)
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if ((target as HTMLElement).style.minHeight) {
              (target as HTMLElement).style.minHeight = ''
            }
            setIsReady(true)
          })
        })
      }

      flickityRef.current.on('ready', handleReady)
      fallbackTimer = setTimeout(handleReady, 100)
      window.addEventListener('resize', handleResize)
      sliderRef.current = target.querySelector('.flickity-slider')
    })

    return () => {
      if (fallbackTimer) clearTimeout(fallbackTimer)
      flickityRef.current?.destroy()
      window.removeEventListener('resize', handleResize)
      setIsReady(false)
    }
  }, deps)

  const SliderPortal = useCallback(
    ({ children }: {children: ComponentChildren}) => {
      if (!target) return children
      return createPortal(children, target)
    },
    [target]
  )

  return {
    flickity: flickityRef.current,
    SliderPortal,
    isReady
  }
}
